import React, { Fragment, useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import Header from '../../../components/Header';
import Appfooter from '../../../components/Appfooter';
import Load from '../../../components/Load';
import { CRIAR_QUADRA, ATUALIZAR_QUADRA, QUADRA_ADMIN } from '../../../services/quadraAdminService';
import { TIPOS_PISO, TIPO_PISO_LABEL } from '../../../services/quadraService';

const AdminQuadraForm: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  const localId = searchParams.get('localId') ?? '';
  const localNome = searchParams.get('localNome') ?? '';

  const [form, setForm] = useState({
    nome: '', descricao: '', tipoPiso: 'SAIBRO', observacao: '', ativa: true,
  });
  const [erro, setErro] = useState('');
  const [uploading, setUploading] = useState(false);
  const [imagens, setImagens] = useState<{ id: string; url: string; ordem: number }[]>([]);

  const { data: quadraData, loading: loadingQuadra } = useQuery(QUADRA_ADMIN, {
    variables: { id },
    skip: !isEdit,
  });

  useEffect(() => {
    const q = quadraData?.quadraPorId;
    if (q) {
      setForm({ nome: q.nome, descricao: q.descricao ?? '', tipoPiso: q.tipoPiso, observacao: q.observacao ?? '', ativa: q.ativa });
      setImagens((q.imagens ?? []).slice().sort((a: any, b: any) => a.ordem - b.ordem));
    }
  }, [quadraData]);

  const [criarQuadra, { loading: criando }] = useMutation(CRIAR_QUADRA);
  const [atualizarQuadra, { loading: atualizando }] = useMutation(ATUALIZAR_QUADRA);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErro('');
    try {
      const input = { ...form, localId };
      if (isEdit) {
        await atualizarQuadra({ variables: { id, input } });
      } else {
        await criarQuadra({ variables: { input } });
      }
      navigate(-1);
    } catch (err: any) {
      setErro(err.message ?? 'Erro ao salvar quadra.');
    }
  };

  const getApiBase = () => {
    const endpoint = process.env.REACT_APP_GRAPHQL_ENDPOINT ?? '';
    return endpoint.replace(/\/graphql\/?$/, '').replace(/\/$/, '');
  };

  const handleUpload = async (file: File) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('arquivo', file);
      const token = localStorage.getItem('token');
      const res = await fetch(`${getApiBase()}/api/quadras/${id}/imagens`, {
        method: 'POST',
        headers: { authorization: token ? `Bearer ${token}` : '' },
        body: formData,
      });
      const text = await res.text();
      if (!res.ok) throw new Error(text || `Erro ${res.status}`);
      const json = JSON.parse(text);
      setImagens(prev => [...prev, { id: json.id, url: json.url, ordem: json.ordem ?? prev.length }].sort((a, b) => a.ordem - b.ordem));
    } catch (err: any) {
      setErro(err.message ?? 'Erro ao fazer upload.');
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = async (imagemId: string) => {
    if (!window.confirm('Remover esta imagem?')) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${getApiBase()}/api/quadras/imagens/${imagemId}`, {
        method: 'DELETE',
        headers: { authorization: token ? `Bearer ${token}` : '' },
      });
      if (!res.ok) throw new Error(`Erro ${res.status}`);
      setImagens(prev => prev.filter(img => img.id !== imagemId));
    } catch (err: any) {
      setErro(err.message ?? 'Erro ao remover imagem.');
    }
  };

  if (loadingQuadra) return <Fragment><Header /><div className="main-content"><Load /></div><Appfooter /></Fragment>;

  return (
    <Fragment>
      <Header />
      <div className="main-content right-chat-active">
        <div className="middle-sidebar-bottom">
          <div className="middle-sidebar-left pe-0">

            <div className="d-flex align-items-center gap-2 mb-3">
              <button className="btn btn-light btn-sm rounded-xl" onClick={() => navigate(-1)}>
                <i className="feather-arrow-left me-1"></i> Voltar
              </button>
              <h2 className="fw-700 font-md text-grey-900 mb-0">
                {isEdit ? 'Editar Quadra' : 'Nova Quadra'}
              </h2>
              {localNome && <span className="font-xssss text-grey-500">— {localNome}</span>}
            </div>

            <div className="row">
              <div className="col-lg-8">
                <div className="card shadow-xss rounded-xxl border-0 p-4 mb-3">
                  <form onSubmit={handleSubmit}>
                    <div className="row">
                      <div className="col-sm-8 mb-3">
                        <label className="font-xssss fw-600 text-grey-600 mb-1 d-block">Nome *</label>
                        <input
                          className="form-control rounded-xl font-xsss"
                          value={form.nome}
                          onChange={e => setForm(f => ({ ...f, nome: e.target.value }))}
                          required
                        />
                      </div>
                      <div className="col-sm-4 mb-3">
                        <label className="font-xssss fw-600 text-grey-600 mb-1 d-block">Tipo de Piso *</label>
                        <select
                          className="form-select rounded-xl font-xsss"
                          value={form.tipoPiso}
                          onChange={e => setForm(f => ({ ...f, tipoPiso: e.target.value }))}
                        >
                          {TIPOS_PISO.map(t => <option key={t} value={t}>{TIPO_PISO_LABEL[t]}</option>)}
                        </select>
                      </div>
                      <div className="col-12 mb-3">
                        <label className="font-xssss fw-600 text-grey-600 mb-1 d-block">Descrição</label>
                        <textarea
                          className="form-control rounded-xl font-xsss"
                          rows={3}
                          value={form.descricao}
                          onChange={e => setForm(f => ({ ...f, descricao: e.target.value }))}
                        />
                      </div>
                      <div className="col-12 mb-3">
                        <label className="font-xssss fw-600 text-grey-600 mb-1 d-block">Observação</label>
                        <input
                          className="form-control rounded-xl font-xsss"
                          value={form.observacao}
                          onChange={e => setForm(f => ({ ...f, observacao: e.target.value }))}
                          placeholder="Ex: Recém reformada, coberta..."
                        />
                      </div>
                      <div className="col-12 mb-3">
                        <div className="form-check">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            id="ativa"
                            checked={form.ativa}
                            onChange={e => setForm(f => ({ ...f, ativa: e.target.checked }))}
                          />
                          <label className="form-check-label font-xsss fw-600" htmlFor="ativa">Quadra ativa</label>
                        </div>
                      </div>
                    </div>

                    {erro && <div className="alert alert-danger font-xssss py-2 mb-3">{erro}</div>}

                    <div className="d-flex gap-2">
                      <button
                        type="submit"
                        className="btn bg-primary-gradiant text-white rounded-xl fw-600 font-xsss px-4"
                        disabled={criando || atualizando}
                      >
                        {criando || atualizando ? 'Salvando...' : isEdit ? 'Salvar alterações' : 'Criar quadra'}
                      </button>
                      <button type="button" className="btn btn-light rounded-xl fw-600 font-xsss px-4" onClick={() => navigate(-1)}>
                        Cancelar
                      </button>
                    </div>
                  </form>
                </div>
              </div>

              {isEdit && (
                <div className="col-lg-4">
                  <div className="card shadow-xss rounded-xxl border-0 p-4">
                    <h5 className="fw-700 font-xss mb-3 text-grey-900">Imagens</h5>
                    {imagens.map(img => (
                      <div key={img.id} className="mb-3 position-relative">
                        <div
                          className="rounded-xl overflow-hidden mb-2"
                          style={{ height: 100, background: `url(${img.url}) center/cover` }}
                        />
                        <button
                          type="button"
                          className="btn btn-danger rounded-xl font-xssss px-2 py-1 position-absolute"
                          style={{ top: 6, right: 6 }}
                          onClick={() => handleRemove(img.id)}
                        >
                          <i className="feather-trash-2"></i>
                        </button>
                      </div>
                    ))}
                    <label className="btn btn-light rounded-xl font-xssss fw-600 w-100 mb-0" style={{ cursor: 'pointer' }}>
                      {uploading ? 'Enviando...' : <><i className="feather-upload me-1"></i> Adicionar imagem</>}
                      <input
                        type="file"
                        accept="image/*"
                        className="d-none"
                        disabled={uploading}
                        onChange={e => e.target.files?.[0] && handleUpload(e.target.files[0])}
                      />
                    </label>
                  </div>
                </div>
              )}
            </div>

          </div>
        </div>
      </div>
      <Appfooter />
    </Fragment>
  );
};

export default AdminQuadraForm;
